import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { FeatureFlag } from '@ndvr/auth-api';
import { ReportingPreset, ReportingSettingsOverrides } from '@ndvr/reporting-api';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/cn';
import { WorkspaceSettingBanner } from '@/views/admin/WorkspaceSettingBanner';
import { useAuthContext } from '@/views/auth/Auth';
import { useFeatureFlag } from '@/views/auth/useFeatureFlag';

import {
    useReportingSettingsPresets,
    useSaveTenantReportingSettings,
    useTenantReportingSettings,
} from './reporting-settings-queries';
import {
    hasReportingSettingsOverrides,
    REPORTING_PRESET_LABELS,
    REPORTING_PRESETS,
    reportingSettingsOverridesEqual,
} from './reporting-settings-state';
import { ReportingSettingsEditor } from './ReportingSettingsEditor';

export const TenantReportingSettingsPage = () => {
    const reportingEnabled = useFeatureFlag(FeatureFlag.CC_REPORTING);

    if (reportingEnabled === undefined) {
        return null;
    }

    if (!reportingEnabled) {
        return <Navigate to="/preferences" replace />;
    }

    return <TenantReportingSettingsPageInternal />;
};

const TenantReportingSettingsPageInternal = () => {
    const { currentTenant, availableTenants, setTenant } = useAuthContext();
    const presetsQuery = useReportingSettingsPresets();
    const settingsQuery = useTenantReportingSettings(currentTenant.tenantGUID);
    const saveMutation = useSaveTenantReportingSettings();
    const [selectedPreset, setSelectedPreset] = useState<ReportingPreset>(ReportingPreset.STANDARD);
    const [overrides, setOverrides] = useState<ReportingSettingsOverrides>({});
    // Distinguishes user edits from the not-yet-hydrated initial draft: hydration from
    // the server must win until the user actually changes something, and after that a
    // background refetch (window focus, another admin's change) must never clobber the
    // draft. A comparison against server data cannot tell those two states apart.
    const [hasUserEdits, setHasUserEdits] = useState(false);

    useEffect(() => {
        setHasUserEdits(false);
    }, [currentTenant.tenantGUID]);

    useEffect(() => {
        if (settingsQuery.data && !hasUserEdits) {
            setSelectedPreset(settingsQuery.data.preset);
            setOverrides(settingsQuery.data.overrides);
        }
    }, [settingsQuery.data, hasUserEdits]);

    const presetSettings = useMemo(
        () => new Map(presetsQuery.data?.map((preset) => [preset.preset, preset.settings])),
        [presetsQuery.data],
    );
    const baseSettings = presetSettings.get(selectedPreset);
    const isCustom = hasReportingSettingsOverrides(overrides);
    const isDirty =
        hasUserEdits &&
        !!settingsQuery.data &&
        (selectedPreset !== settingsQuery.data.preset ||
            !reportingSettingsOverridesEqual(overrides, settingsQuery.data.overrides));
    const isSaving = saveMutation.isPending;

    // The latch is recomputed from a draft-vs-server comparison on every edit so that
    // reverting the draft to the stored state (per-setting reset, re-selecting the
    // stored preset) re-enables server synchronization instead of latching forever.
    const editOverrides = (next: ReportingSettingsOverrides) => {
        setOverrides(next);
        setHasUserEdits(
            !settingsQuery.data ||
                selectedPreset !== settingsQuery.data.preset ||
                !reportingSettingsOverridesEqual(next, settingsQuery.data.overrides),
        );
    };

    const selectPreset = (preset: ReportingPreset) => {
        setSelectedPreset(preset);
        setOverrides({});
        setHasUserEdits(
            !settingsQuery.data ||
                preset !== settingsQuery.data.preset ||
                hasReportingSettingsOverrides(settingsQuery.data.overrides),
        );
    };

    const cancelChanges = () => {
        if (!settingsQuery.data) {
            return;
        }
        setSelectedPreset(settingsQuery.data.preset);
        setOverrides(settingsQuery.data.overrides);
        setHasUserEdits(false);
    };

    const saveChanges = () => {
        if (!settingsQuery.data || !isDirty) {
            return;
        }
        saveMutation.mutate(
            {
                tenantGUID: currentTenant.tenantGUID,
                preset: selectedPreset,
                overrides,
            },
            {
                onSuccess: (saved) => {
                    setSelectedPreset(saved.preset);
                    setOverrides(saved.overrides);
                    setHasUserEdits(false);
                },
            },
        );
    };

    if (settingsQuery.isError || presetsQuery.isError) {
        return (
            <div className="text-destructive py-12 text-center text-sm">
                Failed to load reporting settings.
                <Button
                    variant="link"
                    className="ml-2 h-auto p-0"
                    onClick={() => {
                        void settingsQuery.refetch();
                        void presetsQuery.refetch();
                    }}
                >
                    Retry
                </Button>
            </div>
        );
    }

    if (settingsQuery.isPending || presetsQuery.isPending || !baseSettings) {
        return <div className="text-cocoa-500 py-12 text-center text-sm">Loading reporting settings...</div>;
    }

    return (
        <div className="mx-auto flex w-full max-w-5xl flex-col pb-6">
            <WorkspaceSettingBanner
                resourceLabel="Reporting settings"
                availableTenants={availableTenants}
                currentTenant={currentTenant}
                disabled={isSaving || isDirty}
                onTenantChange={setTenant}
            />

            <div className="mb-7 flex flex-col gap-5">
                <div className="flex flex-wrap items-center gap-3">
                    <h2 className="font-heading text-cocoa-1000 text-3xl">{currentTenant.name}</h2>
                    <Badge className="bg-gold-100 text-gold-600 border-0 px-3 py-1">
                        {isCustom
                            ? `Custom · ${REPORTING_PRESET_LABELS[selectedPreset]} base`
                            : REPORTING_PRESET_LABELS[selectedPreset]}
                    </Badge>
                </div>

                <div>
                    <div className="flex flex-wrap gap-3">
                        {REPORTING_PRESETS.map((preset) => {
                            const selected = preset === selectedPreset;
                            return (
                                <Button
                                    key={preset}
                                    type="button"
                                    variant={selected ? 'highlighted' : 'outline2'}
                                    className={cn('min-w-32 px-6 text-base', selected && 'bg-cocoa-900')}
                                    disabled={isSaving}
                                    aria-pressed={selected}
                                    onClick={() => selectPreset(preset)}
                                >
                                    {REPORTING_PRESET_LABELS[preset]}
                                </Button>
                            );
                        })}
                    </div>
                    <p className="text-cocoa-400 mt-4 text-sm">
                        Choose a preset to set everything at once, or adjust individual settings below to create a
                        custom configuration.
                    </p>
                </div>
            </div>

            <ReportingSettingsEditor
                baseSettings={baseSettings}
                overrides={overrides}
                disabled={isSaving}
                onOverridesChange={editOverrides}
            />

            <div className="mt-8 flex min-h-10 items-center gap-3">
                {isDirty && <p className="text-cocoa-500 mr-auto text-sm">You have unsaved changes.</p>}
                <Button variant="outline2" disabled={!isDirty || isSaving} onClick={cancelChanges}>
                    Cancel
                </Button>
                <Button variant="highlighted" disabled={!isDirty || isSaving} onClick={saveChanges}>
                    {isSaving ? 'Saving...' : 'Save'}
                </Button>
            </div>
        </div>
    );
};
